import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  Row,
  HeadingDisplay,
  ListDisplay,
  SkeletonLoader,
  ButtonContainer,
  ButtonPrimary,
  MessageFoundation as Message
} from '@warrenbrasil/nebraska-web';
import { InputMoney } from '@/modules/transaction/components/InputMoney';
import { RiskSignature } from '@/modules/common/components/RiskSignature';

import {
  getProductInvestmentAmount,
  getInvestmentAmountIncrement,
  riskSignature,
  IInvestmentDetails,
  IRiskSignature,
  IncrementOperation
} from '@/modules/transaction/services/product-investment';

import { errorText, errorStatus, listDetails } from './helpers';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { EProductTypes } from '@/modules/catalog/services/products';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';


export default /*#__PURE__*/_defineComponent({
  __name: 'InvestmentStep',
  setup(__props) {

const router = useRouter();
const route = useRoute();

const investmentDetails = inject(
  'investmentDetails'
) as Ref<IInvestmentDetails>;
const riskSignatureData = inject('riskSignatureData') as Ref<IRiskSignature>;
const productId = inject('productId') as Ref<string>;

const isFetchingReview = inject('isFetchingReview') as Ref<boolean>;

const fetchInvestmentReview = inject(
  'fetchInvestmentReview',
  (amount: number) => ({
    amount
  })
);

const isRiskSignatureOpen = ref(false);
const isLoading = ref(false);
const updateValue = ref(false);
const isButtonDisabled = ref(false);
const isCorrectedValue = ref(false);

const isLoadingAmount = ref(false);
const quantity = ref(0);

const investmentInput = ref({
  value: 0,
  formatted: ''
});

const hasBalance = computed(() => {
  return investmentDetails.value.accountBalance.value > 0;
});

const balanceTitle = computed(() => {
  return hasBalance.value ? 'Saldo em conta' : 'Você não possui saldo';
});

const balanceValue = computed(() => {
  return hasBalance.value
    ? investmentDetails.value.accountBalance.formatted
    : 'Depositar';
});

const headerValueClass = computed(() => {
  return hasBalance.value ? '' : 'investment-step__header--value';
});

const productType = computed(() => {
  const { type } = route.params;
  return type;
});

const status = computed(() => {
  return errorStatus(
    investmentDetails.value,
    investmentInput.value.value,
    quantity.value,
    isEnabledSecondaryMarket.value
  );
});

const helperText = computed(() => {
  return errorText(
    investmentDetails.value,
    investmentInput.value.value,
    isCorrectedValue.value,
    quantity.value,
    isEnabledSecondaryMarket.value
  );
});

const details = computed(() => {
  return listDetails(
    investmentDetails.value,
    productType.value,
    quantity.value,
    isLoadingAmount.value,
    isEnabledSecondaryMarket.value,
    investmentDetails.value.maximumInvestmentQuantity?.value
  );
});

const showButtons = computed(() => {
  return productType.value !== EProductTypes.FUNDS;
});

const isEnabledSecondaryMarket = computed(() => {
  return FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_SECONDARY_MARKET);
});

const showFooterMessage = computed(() => {
  return (
    productType.value === EProductTypes.FIXED_INCOME &&
    isEnabledSecondaryMarket.value
  );
});

const shortcuts = computed(() => {
  return productType.value === EProductTypes.FUNDS
    ? investmentDetails.value.buttons
    : [];
});

const incrementValue = computed(() => {
  return investmentDetails.value.increment.value;
});

const minimumInvestment = computed(() => {
  return investmentDetails.value.minimumInvestment.value;
});

const minimumInvestmentQuantity = computed(() => {
  return investmentDetails.value.minimumInvestmentQuantity;
});

const hasInsufficientBalance = computed(() => {
  return (
    !hasBalance.value ||
    investmentDetails.value.accountBalance.value < investmentInput.value.value
  );
});

const isDisabled = computed(() => {
  return (
    investmentInput.value.value <= 0 ||
    status.value === 'error' ||
    isLoadingAmount.value ||
    isButtonDisabled.value
  );
});

function onChangeValue(data: {
  value: number;
  formatted: string;
  increment: boolean;
  operation?: IncrementOperation;
}) {
  investmentInput.value = data;
  isCorrectedValue.value = false;

  if (productType.value !== EProductTypes.FUNDS) {
    isButtonDisabled.value = true;

    if (data.operation) {
      fetchAmountIncrement(data.operation);
    } else if (
      data.increment &&
      investmentInput.value.value >=
        investmentDetails.value.minimumInvestment.value
    ) {
      fetchInvestmentAmount(data.value);
    } else {
      investmentInput.value = {
        value: data.value,
        formatted: data.formatted
      };
      quantity.value = 0;
    }
  }
}

function handleBlur() {
  if (
    productType.value === EProductTypes.FUNDS ||
    investmentInput.value.value <
      investmentDetails.value.minimumInvestment.value
  ) {
    quantity.value = 0;
    return;
  }

  fetchInvestmentAmount(investmentInput.value.value);
}

function investClick() {
  if (hasInsufficientBalance.value) {
    router.push({ name: 'deposit-pix', params: { cameFrom: route.fullPath } });
    return;
  }

  if (riskSignatureData.value.requestRisksSignature.length > 0) {
    isRiskSignatureOpen.value = true;
    return;
  }

  fetchInvestmentReview(investmentInput.value.value);
}

async function fetchInvestmentAmount(amount: number) {
  updateValue.value = false;
  isLoadingAmount.value = true;

  try {
    const data = await getProductInvestmentAmount(productId.value, amount);

    investmentInput.value = data.amount;
    quantity.value = data.quantity.value;

    updateValue.value = true;
    isButtonDisabled.value = false;
    isCorrectedValue.value = true;
  } catch (e) {
    console.error(e);
  } finally {
    isLoadingAmount.value = false;
  }
}

async function fetchAmountIncrement(operation?: IncrementOperation) {
  updateValue.value = false;
  isLoadingAmount.value = true;

  const incrementQuantity =
    minimumInvestmentQuantity.value > 1 ? 1 : minimumInvestmentQuantity.value;

  try {
    const data = await getInvestmentAmountIncrement(
      investmentDetails.value.increment.value,
      incrementQuantity,
      quantity.value,
      operation
    );

    investmentInput.value = {
      value: data.result.value,
      formatted: data.result.formatted
    };
    quantity.value = data.quantity;

    updateValue.value = true;
    isButtonDisabled.value = false;
  } catch (e) {
    console.error(e);
  } finally {
    isLoadingAmount.value = false;
  }
}

async function acceptRisk() {
  isRiskSignatureOpen.value = false;

  try {
    isLoading.value = true;

    await riskSignature(productId.value);

    if (hasInsufficientBalance.value) {
      router.push({
        name: 'deposit-pix',
        params: { cameFrom: route.fullPath }
      });
    } else {
      fetchInvestmentReview(investmentInput.value.value);
    }
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao aceitar os riscos. Aguarde alguns instantes e tente novamente.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
  }
}

function redirectToDeposit() {
  if (hasBalance.value) return;

  router.push({ name: 'deposit-pix' });
}

return { __sfc: true,router, route, investmentDetails, riskSignatureData, productId, isFetchingReview, fetchInvestmentReview, isRiskSignatureOpen, isLoading, updateValue, isButtonDisabled, isCorrectedValue, isLoadingAmount, quantity, investmentInput, hasBalance, balanceTitle, balanceValue, headerValueClass, productType, status, helperText, details, showButtons, isEnabledSecondaryMarket, showFooterMessage, shortcuts, incrementValue, minimumInvestment, minimumInvestmentQuantity, hasInsufficientBalance, isDisabled, onChangeValue, handleBlur, investClick, fetchInvestmentAmount, fetchAmountIncrement, acceptRisk, redirectToDeposit, NebraskaColors, BaseText, Row, HeadingDisplay, ListDisplay, SkeletonLoader, ButtonContainer, ButtonPrimary, Message, InputMoney, RiskSignature }
}

})